import Phaser from "phaser";
import { ART_RES } from "../art/bake.ts";
import { COIN_RADIUS, ensureCoinTexture, ensureFuelCanTexture } from "../art/pickupArt.ts";

export type PickupKind = "fuel" | "coin";

/**
 * A simple collectible: a static sensor body plus a canvas-baked sprite. Detected via overlap
 * checks in the owning system, so pickups never physically interact with the vehicle.
 */
export class Pickup {
  readonly body: MatterJS.BodyType;
  readonly kind: PickupKind;
  readonly value: number;
  private readonly image: Phaser.GameObjects.Image;
  private collected = false;

  constructor(scene: Phaser.Scene, kind: PickupKind, value: number, x: number, y: number) {
    this.kind = kind;
    this.value = value;
    this.body = scene.matter.bodies.circle(x, y, kind === "coin" ? COIN_RADIUS : 18, {
      isStatic: true,
      isSensor: true,
      label: `pickup-${kind}`,
    });
    this.body.plugin = { pickup: this };
    scene.matter.world.add(this.body);

    const key = kind === "coin" ? ensureCoinTexture(scene, value) : ensureFuelCanTexture(scene);
    this.image = scene.add.image(x, y, key).setScale(1 / ART_RES).setDepth(5);

    if (kind === "fuel") {
      // Gentle bob so the can catches the eye.
      scene.tweens.add({
        targets: this.image,
        y: y - 5,
        duration: 700,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });
    }
  }

  get position(): { x: number; y: number } {
    return this.body.position;
  }

  get isCollected(): boolean {
    return this.collected;
  }

  collect(scene: Phaser.Scene): void {
    if (this.collected) return;
    this.collected = true;
    if (scene.matter.world) scene.matter.world.remove(this.body);
    scene.tweens.killTweensOf(this.image);
    this.image.destroy();
  }
}
